import { useEffect, useRef } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { DEMO_BANNER_TEXT } from "../api";
import { useHealth } from "../api/hooks";
import { LiveProvider } from "../live/LiveProvider";
import { Banner } from "../ui/Banner";
import { Icon } from "../ui/Icon";
import { ToastHost } from "../ui/Toast";
import { ResetNotice } from "./ResetNotice";
import { useCurrentMe } from "./auth";
import { useDisciplineFilterSync } from "./disciplineFilter";
import { AvatarMenu } from "./frame/AvatarMenu";
import { DisciplineChips } from "./frame/DisciplineChips";
import { findScreen } from "./screens";
import { AgentDock, AgentLauncher, useShellAgent } from "./ShellAgent";
import { FilesPill, StatusFacts, useAppProblems } from "./StatusBar";
import { TopNav } from "./TopNav";
import { useViewBadges } from "./viewBadges";
import { WorkspaceSwitch } from "./WorkspaceSwitch";
import "./shell.css";
import "./frame/frame.css";

/**
 * The frame around every screen after sign-in (rebuild package 1B): a 40 px bar and, under it, the region of the screen. The page
 * itself never scrolls. An older screen scrolls inside the region; a view of the new design (`layout: "view"` in screens.ts)
 * fills it and scrolls inside each of its own panes.
 *
 * The bar holds the view tabs and the More menu, the discipline filter, the agent button (older screens only: a view has its own
 * chat), the help icon (Getting started) and the account menu. The demo banner, the practice note and the other notices sit
 * above the region. The live channel, the toasts, the skip link and the move of the focus to the screen on each navigation
 * are kept from the shell this one replaces.
 */
export function Shell() {
  const me = useCurrentMe();
  const health = useHealth();
  const { pathname } = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const firstPath = useRef(pathname);
  const agent = useShellAgent(me);
  const badges = useViewBadges(me);
  const problems = useAppProblems(me);
  useDisciplineFilterSync();

  // After the person goes to another screen, keyboard and screen reader users start at its top, and so does the screen itself.
  useEffect(() => {
    if (firstPath.current === pathname) return;
    firstPath.current = pathname;
    mainRef.current?.focus({ preventScroll: true });
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [pathname]);

  const layout = findScreen(pathname)?.layout ?? "page";
  // A view has the chat of its own: the shell's agent button and panel are for the older screens.
  const shellAgent = layout === "view" ? { ...agent, target: null, open: false } : agent;

  const demo = me.agent.demo || health.data?.agent?.demo === true;
  const active = me.active;
  const practice = active?.space === "practice";
  const closed = active ? me.memberships.find((m) => m.id === active.membership_id)?.workspace_status === "closed" : false;
  // The discipline switch is in the account menu, except where the person must use it now: to choose a discipline, or to leave
  // the practice space.
  const switchInBar = practice || (!active && me.memberships.length > 0);

  return (
    <LiveProvider me={me}>
      <div className="frame">
        <a className="skip-link" href="#main">
          Skip to the page
        </a>
        <header className="frame__bar">
          <NavLink className="frame__brand" to="/" end>
            E-NEX
          </NavLink>
          <TopNav me={me} badges={badges} />
          <span className="frame__grow" />
          {switchInBar && <WorkspaceSwitch me={me} />}
          {active && !practice && <DisciplineChips />}
          <AgentLauncher agent={shellAgent} />
          <NavLink className="frame__help" to="/getting-started" aria-label="Getting started" title="Getting started">
            <Icon name="help" size={16} />
          </NavLink>
          <AvatarMenu me={me} withSwitch={!switchInBar} problems={problems}>
            {active && <FilesPill />}
            <StatusFacts me={me} />
          </AvatarMenu>
        </header>

        <div className="frame__body shell__body" data-agent={shellAgent.open ? "open" : undefined}>
          <main id="main" tabIndex={-1} ref={mainRef} className="frame__main">
            {/* The notices stand above the screen, so a view keeps its one root; the strip is not drawn while it is empty. */}
            <div className="frame__notices">
              {demo && <Banner tone="demo">{DEMO_BANNER_TEXT}</Banner>}
              {health.isError && <Banner tone="warning">The back end is not reachable. What you see may be out of date.</Banner>}
              {closed && <Banner>This discipline is closed. You can read its work, but you cannot change it.</Banner>}
              {practice && (
                <Banner tone="note">
                  You are in your practice space for {active?.workspace_name}. Nothing you do here reaches the discipline or its files. It stays as you leave it, also after the app has been restarted.
                </Banner>
              )}
              <ResetNotice me={me} />
            </div>
            <div ref={scrollRef} className="frame__scroll" data-layout={layout}>
              {/* An older screen is a padded column (session.css widens it for the session screens); a view fills the region. */}
              <div className={layout === "view" ? "frame__view" : "frame__page shell__main stack"}>
                <Outlet />
              </div>
            </div>
          </main>
          <AgentDock agent={shellAgent} />
        </div>

        <ToastHost />
      </div>
    </LiveProvider>
  );
}
